import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface GalleryImage {
  id: number;
  url: string;
  alt: string;
  label: string;
  caption: string;
}

interface GallerySectionProps {
  images: GalleryImage[];
}

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); observer.disconnect(); };
  }, [threshold]);
  return { ref, isVisible };
}

interface GalleryItemProps {
  image: GalleryImage;
  delayClass?: string;
  heightClass: string;
}

function GalleryItem({ image, delayClass = '', heightClass }: GalleryItemProps) {
  const { ref, isVisible } = useReveal();

  return (
    <div
      ref={ref}
      className={`reveal-up ${delayClass} ${isVisible ? 'visible' : ''} flex flex-col`}
    >
      {/* Image wrapper */}
      <div className={`relative overflow-hidden group cursor-pointer ${heightClass} w-full`}>
        <img
          src={image.url}
          alt={image.alt}
          className="w-full h-full object-cover object-center transition-transform duration-1000 ease-out group-hover:scale-[1.04]"
        />
        {/* Dark overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-700" />
        {/* Number badge top-left */}
        <div className="absolute top-5 left-5 z-10">
          <span className="font-sans text-[10px] tracking-[0.25em] text-white/70 group-hover:text-[#B59A6A] transition-colors duration-300">
            {image.label}
          </span>
        </div>
      </div>
      {/* Caption below */}
      <div className="flex items-center gap-3 mt-4">
        <div className="w-4 h-px bg-[#B59A6A] shrink-0" />
        <p className="font-sans text-[11px] tracking-[0.18em] uppercase text-[#6B6460]">
          {image.caption}
        </p>
      </div>
    </div>
  );
}

export default function GallerySection({ images }: GallerySectionProps) {
  const { t } = useTranslation();
  const { ref: refHead, isVisible: visHead } = useReveal(0.2);

  const img1 = images[0];
  const img2 = images[1];
  const img3 = images[2];
  const img4 = images[3];
  const img5 = images[4];
  const img6 = images[5];

  return (
    <section id="gallery" className="py-28 bg-[#F5F3EF]">
      <div className="max-w-7xl mx-auto px-8">

        {/* ── Header ── */}
        <div
          ref={refHead}
          className={`mb-16 reveal-up ${visHead ? 'visible' : ''}`}
        >
          <div className="flex items-center gap-4 mb-5">
            <div className="gold-line" />
            <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">
              {t('gallery.label')}
            </p>
          </div>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-[#1C1C1C]">
              {t('gallery.heading')}
            </h2>
            <p className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#B59A6A] mb-1">
              {t('gallery.rooms')}
            </p>
          </div>
          <div className="mt-6 w-full h-px bg-[#E5DDD3]" />
        </div>

        {/* ── Row 1: Large landscape (3/5) + tall portrait (2/5) ── */}
        <div className="grid grid-cols-5 gap-3 mb-12">
          {img1 && <div className="col-span-3"><GalleryItem image={img1} delayClass="reveal-delay-1" heightClass="h-[500px]" /></div>}
          {img2 && <div className="col-span-2"><GalleryItem image={img2} delayClass="reveal-delay-2" heightClass="h-[500px]" /></div>}
        </div>

        {/* ── Row 2: Three equal columns ── */}
        <div className="grid grid-cols-3 gap-3 mb-12">
          {img3 && <GalleryItem image={img3} delayClass="reveal-delay-1" heightClass="h-[320px]" />}
          {img4 && <GalleryItem image={img4} delayClass="reveal-delay-2" heightClass="h-[320px]" />}
          {img5 && <GalleryItem image={img5} delayClass="reveal-delay-3" heightClass="h-[320px]" />}
        </div>

        {/* ── Row 3: Full-width panoramic statement ── */}
        {img6 && <GalleryItem image={img6} delayClass="reveal-delay-1" heightClass="h-[460px]" />}

        {/* ── Footer note ── */}
        <div className="flex items-center justify-between mt-14 pt-8 border-t border-[#E5DDD3]">
          <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#BFBAB5]">
            {t('gallery.footer.note')}
          </p>
          <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#B59A6A] cursor-pointer hover:opacity-70 transition-opacity">
            {t('gallery.footer.more')}
          </p>
        </div>

      </div>
    </section>
  );
}
